import { Box, RingProgress, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";


import { KpiDashboardResponse } from "types";

import classes from "./kpi.module.css";
import {
  KPI_PROGRESS_TRACK,
  KPI_RING_GRADIENT_FROM,
  KPI_RING_GRADIENT_TO,
  KPI_TEXT_DARK,
  KPI_TEXT_GRAY,
} from "./kpiStatus";

interface KpiProgressProps {
  dashboard: KpiDashboardResponse;
}

// The target marker sits at this fraction of the bar width, leaving a shaded
// "beyond target" tail to the right. The red fill grows into the tail when the
// target is exceeded but is capped so it never fills the bar completely.
const TARGET_POSITION = 0.72;
const MAX_FILL = 0.94;

const formatValue = (value: number, unit?: string | null) =>
  unit === "CHF" ? `${value.toLocaleString("de-CH")} ${unit}` : String(value);

const KpiProgress = ({ dashboard }: KpiProgressProps) => {
  const { t } = useTranslation();

  const { value, unit } = dashboard;

  // Progress is shown only for metrics with a target (spec).
  if (dashboard.target === null || dashboard.target === undefined) {
    return null;
  }

  const target = dashboard.target;
  const ratio = target > 0 ? value / target : 0;
  const reached = value >= target;

  // Yellow gradient ring, used once the target is reached (always full).
  const renderRing = (fillPercent: number) => (
    <Box className={classes.card}>
      <Text className={classes.cardTitle}>{t("profilePage.kpi.progress.title")}</Text>

      <Box className={classes.ringWrap}>
        {/* SVG gradient definition referenced by the RingProgress stroke below.
            RingProgress section colors don't accept CSS gradients, so we use url(#id). */}
        <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden>
          <defs>
            <linearGradient id="kpiRingGradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={KPI_RING_GRADIENT_FROM} />
              <stop offset="100%" stopColor={KPI_RING_GRADIENT_TO} />
            </linearGradient>
          </defs>
        </svg>

        <RingProgress
          size={200}
          thickness={16}
          roundCaps
          rootColor={KPI_PROGRESS_TRACK}
          sections={[
            {
              value: fillPercent,
              color: "url(#kpiRingGradient)",
            },
          ]}
          label={
            <Box ta="center">
              {reached ? (
                <>
                  <Text size="sm" c={KPI_TEXT_GRAY}>
                    {t("profilePage.kpi.progress.targetLabel")}
                  </Text>
                  <Text fw={700} fz={22} c={KPI_TEXT_DARK}>
                    {t("profilePage.kpi.progress.reached")}
                  </Text>
                </>
              ) : null}
              <Text size={reached ? "sm" : "xl"} fw={reached ? 400 : 700} c={reached ? KPI_TEXT_GRAY : KPI_TEXT_DARK}>
                {`${formatValue(value, unit)} / ${formatValue(target, unit)}`}
              </Text>
            </Box>
          }
        />
      </Box>
    </Box>
  );

  // Target reached -> full yellow ring.
  if (reached) {
    return renderRing(100);
  }

  // Below target -> custom horizontal bar with a shaded "beyond target" tail.
  // Red fill is proportional to ratio, scaled so ratio=1 lands on the target marker,
  // then capped at MAX_FILL so the tail is always visible.
  const fillPct = Math.min(ratio * TARGET_POSITION, MAX_FILL) * 100;
  const targetPct = TARGET_POSITION * 100;

  return (
    <Box className={classes.card}>
      <Box className={classes.progressArea}>
        <Box className={classes.progressTrack}>
          {/* Shaded "beyond target" zone. */}
          <Box className={classes.progressShade} style={{ left: `${targetPct}%` }} />
          {/* Red fill. */}
          <Box className={classes.progressFill} style={{ width: `${fillPct}%` }} />

          {/* Realized badge (rounded-top pill on the bar). */}
          <Box className={classes.progressBadge} style={{ left: `${fillPct}%` }}>
            {formatValue(value, unit)}
          </Box>
          {/* Target badge. */}
          <Box
            className={`${classes.progressBadge} ${classes.progressBadgeTarget}`}
            style={{ left: `${targetPct}%` }}
          >
            {formatValue(target, unit)}
          </Box>
        </Box>

        {/* Captions below the bar. */}
        <Box className={classes.progressCaptions}>
          <Text size="sm" c={KPI_TEXT_GRAY} style={{ left: `${fillPct}%` }} className={classes.progressCaption}>
            {t("profilePage.kpi.progress.realized")}
          </Text>
          <Text size="sm" c={KPI_TEXT_GRAY} style={{ left: `${targetPct}%` }} className={classes.progressCaption}>
            {t("profilePage.kpi.progress.target")}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

export default KpiProgress;
